import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Plus, 
  Search, 
  Filter, 
  Download, 
  Eye, 
  Edit, 
  Trash2, 
  FileText, 
  CheckCircle,
  AlertTriangle,
  Clock,
  Building,
  User,
  Phone,
  Mail,
  MapPin,
  Star,
  TrendingUp,
  Users,
  DollarSign,
  BarChart3,
  PieChart,
  TrendingDown,
  MoreHorizontal,
  ArrowLeft,
  Settings,
  ChevronUp,
  ChevronDown,
  RefreshCw,
  HelpCircle
} from 'lucide-react';
import { useToast } from '../common/Toast';
import DataTable from '../common/DataTable';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import api from '../../services/api';

const ReportsAnalytics = () => {
  const navigate = useNavigate();
  const { success, error: showError } = useToast();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [reportsData, setReportsData] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    itemsPerPage: 10,
    totalItems: 0
  });

  // ActionDropdown component for actions
  const ActionDropdown = ({ report, onView, onEdit, onDelete }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [openUpward, setOpenUpward] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };

    const handleScroll = () => setIsOpen(false);

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('scroll', handleScroll, true);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen]);

  const handleToggle = () => {
    if (!isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      setOpenUpward(spaceBelow < 180);
    }
    setIsOpen((v) => !v);
  };

  const handleView = () => {
    setIsOpen(false);
    report?.id && onView(report);
  };

  const handleEdit = () => {
    setIsOpen(false);
    report?.id && onEdit(report);
  };

  const handleDelete = () => {
    setIsOpen(false);
    report?.id && onDelete(report);
  };

  const rect = buttonRef.current?.getBoundingClientRect();

  return (
    <div
      ref={dropdownRef}
      className="relative"
      style={{ zIndex: isOpen ? 9999 : 'auto' }}
    >
      <button
        ref={buttonRef}
        type="button"
        onClick={handleToggle}
        className="inline-flex items-center justify-center w-8 h-8 border border-gray-200 bg-white text-blue-500 rounded hover:bg-blue-50 hover:border-blue-500 transition-colors shadow-sm"
        style={{ position: 'relative', zIndex: isOpen ? 10000 : 'auto' }}
      >
        <MoreHorizontal size={16} />
      </button>

      {isOpen && rect && (
        <div
          className="fixed min-w-[180px] bg-white border border-gray-200 rounded-lg shadow-lg"
          style={{
            zIndex: 10001,
            top: openUpward
              ? window.innerHeight - rect.top + 8
              : rect.bottom + 8,
            left: rect.left,
            transform: 'translateX(-75%)',
          }}
        >
          <ul className="py-1 m-0 list-none">
            <li>
              <button
                onClick={handleView}
                className="flex w-full items-center px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 text-left"
              >
                <Eye size={16} className="mr-2.5 text-gray-500" />
                View Report
              </button>
            </li>

            <li>
              <button
                onClick={handleEdit}
                className="flex w-full items-center px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 text-left"
              >
                <Edit size={16} className="mr-2.5 text-gray-500" />
                Edit
              </button>
            </li>

            <li className="border-t border-gray-200">
              <button
                onClick={handleDelete}
                className="flex w-full items-center px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 text-left"
              >
                <Trash2 size={16} className="mr-2.5 text-red-600" />
                Delete
              </button>
            </li>
          </ul>
        </div>
      )}
    </div>
    );
  };



  useEffect(() => {
    loadReportsData();
  }, []);

  const loadReportsData = async () => {
    setLoading(true);
    setError(null);
    try {
      console.log('[ReportsAnalytics] Fetching reports from backend...');
      const response = await api.get('/reports');
      console.log('[ReportsAnalytics] API response:', response);

      if (response && response.data) {
        // Handle both wrapped ({success, data}) and direct array responses
        let reportsArray = [];
        
        if (Array.isArray(response.data)) {
          reportsArray = response.data;
        } else if (response.data.success && Array.isArray(response.data.data)) {
          reportsArray = response.data.data;
        } else if (Array.isArray(response.data.data)) {
          reportsArray = response.data.data;
        }

        console.log('[ReportsAnalytics] Fetched', reportsArray.length, 'reports');

        // Transform API response to match table format
        const transformedReports = reportsArray.map((report) => ({
          id: report._id || report.id,
          reportName: report.name || report.reportName || 'Untitled Report',
          reportType: report.type || report.reportType || 'Standard',
          description: report.description || '',
          status: report.status || 'Generated',
          generatedDate: report.createdAt || report.generatedDate,
          generatedBy: report.createdBy ? 'User' : (report.generatedBy || 'System'),
          fileSize: report.fileSize || null,
          format: report.format || (report.type === 'Excel' ? 'Excel' : 'PDF'),
          downloadCount: report.downloadCount || 0,
          lastAccessed: report.lastAccessed || null,
          schedule: report.schedule || 'On Demand',
          nextGeneration: report.nextGeneration || new Date().toISOString()
        }));

        setReportsData(transformedReports);
        setPagination(prev => ({
          ...prev,
          totalItems: transformedReports.length
        }));
        
        if (transformedReports.length > 0) {
          success(`Loaded ${transformedReports.length} reports`);
        }
      } else {
        setReportsData([]);
        setError('No data received from server');
      }
    } catch (err) {
      console.error('[ReportsAnalytics] Error loading reports:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to load reports';
      setError(errorMessage);
      showError(errorMessage);
      setReportsData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    loadReportsData();
  };

  // Handle help button click
  const handleHelpClick = () => {
    const helpUrl = '/help/reports-analytics';
    const helpWindow = window.open(
      helpUrl,
      'helpWindow',
      'width=1200,height=800,scrollbars=yes,resizable=yes,menubar=no,toolbar=no,location=no,status=no'
    );
    if (helpWindow) {
      helpWindow.focus();
    }
  };

  // Define action buttons for PageHeader
  const headerActions = [
    {
      label: '', // Empty label for icon-only square button
      icon: <HelpCircle size={18} />,
      onClick: handleHelpClick,
      variant: 'outline',
      tooltip: 'Help (Press ? for keyboard shortcut)'
    },
    {
      label: '', // Empty label for icon-only square button
      icon: <RefreshCw size={18} className={loading ? 'spinning' : ''} />,
      onClick: handleRefresh,
      variant: 'outline',
      tooltip: 'Refresh reports data',
      disabled: loading
    },
    {
      label: 'Generate Report',
      icon: <Plus size={18} />,
      onClick: () => navigate('/subcontracting/reports/create'),
      variant: 'primary',
      tooltip: 'Generate a new report'
    }
  ];

  const handleView = (report) => {
    navigate(`/subcontracting/reports/view/${report.id}`);
  };

  const handleEdit = (report) => {
    navigate(`/subcontracting/reports/edit/${report.id}`);
  };

  const handleDelete = (report) => {
    if (window.confirm(`Are you sure you want to delete report ${report.reportName}?`)) {
      // Simulate delete
      setReportsData(prev => prev.filter(r => r.id !== report.id));
      success('Report deleted successfully!');
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'Generated': { class: 'bg-success', text: 'Generated' },
      'In Progress': { class: 'bg-info', text: 'In Progress' },
      'Failed': { class: 'bg-danger', text: 'Failed' },
      'Scheduled': { class: 'bg-warning', text: 'Scheduled' }
    };
    
    const config = statusConfig[status] || { class: 'bg-secondary', text: status };
    return <span className={`badge ${config.class}`}>{config.text}</span>;
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'Performance':
        return <TrendingUp size={16} className="text-primary" />;
      case 'Financial':
        return <DollarSign size={16} className="text-success" />;
      case 'Compliance':
        return <CheckCircle size={16} className="text-warning" />;
      case 'Resource':
        return <Users size={16} className="text-info" />;
      case 'Safety':
        return <AlertTriangle size={16} className="text-danger" />;
      default:
        return <FileText size={16} className="text-muted" />;
    }
  };

  const getFormatBadge = (format) => {
    const formatConfig = {
      'PDF': { class: 'bg-danger', text: 'PDF' },
      'Excel': { class: 'bg-success', text: 'Excel' },
      'CSV': { class: 'bg-info', text: 'CSV' },
      'Word': { class: 'bg-primary', text: 'Word' }
    };
    
    const config = formatConfig[format] || { class: 'bg-secondary', text: format };
    return <span className={`badge ${config.class}`}>{config.text}</span>;
  };

  const columns = [
    {
      key: '__spacer__',
      label: '',
      width: '16px',
      sortable: false,
      render: () => <div style={{ width: '16px' }} />
    },
    {
      key: 'reportName',
      label: 'Report Name',
      render: (value, report) => (
        <div>
          <div className="fw-bold text-truncate" style={{ maxWidth: '200px' }} title={report.reportName}>
            {report.reportName}
          </div>
          <small className="text-muted text-truncate d-block" style={{ maxWidth: '200px' }} title={report.description}>
            {report.description}
          </small>
        </div>
      )
    },
    {
      key: 'reportType',
      label: 'Type',
      render: (value, report) => (
        <span className="badge bg-light text-dark text-truncate d-inline-block" style={{ maxWidth: '100px' }} title={report.reportType}>
          {report.reportType}
        </span>
      )
    },
    {
      key: 'status',
      label: 'Status',
      render: (value, report) => getStatusBadge(report.status)
    },
    {
      key: 'generatedDate',
      label: 'Generated',
      render: (value, report) => (
        <div>
          {report.generatedDate ? (
            <>
              <div className="fw-medium text-truncate d-block" style={{ maxWidth: '120px' }} title={new Date(report.generatedDate).toLocaleDateString()}>
                {new Date(report.generatedDate).toLocaleDateString()}
              </div>
              <small className="text-muted text-truncate d-block" style={{ maxWidth: '120px' }} title={`By: ${report.generatedBy}`}>
                By: {report.generatedBy}
              </small>
            </>
          ) : (
            <span className="text-muted">Not generated</span>
          )}
        </div>
      )
    },
    {
      key: 'fileSize',
      label: 'File Info',
      render: (value, report) => (
        <div>
          {report.fileSize ? (
            <>
              <div className="fw-medium text-truncate d-block" style={{ maxWidth: '100px' }} title={report.fileSize}>
                {report.fileSize}
              </div>
              {getFormatBadge(report.format)}
            </>
          ) : (
            <span className="text-muted">N/A</span>
          )}
        </div>
      )
    },
    {
      key: 'downloadCount',
      label: 'Downloads',
      render: (value, report) => (
        <div>
          <div className="fw-bold text-truncate d-block" style={{ maxWidth: '100px' }} title={report.downloadCount}>
            {report.downloadCount}
          </div>
          <small className="text-muted">Total</small>
        </div>
      )
    },
    {
      key: 'schedule',
      label: 'Schedule',
      render: (value, report) => (
        <div>
          <div className="fw-medium text-truncate d-block" style={{ maxWidth: '120px' }} title={report.schedule}>
            {report.schedule}
          </div>
          <small className="text-muted text-truncate d-block" style={{ maxWidth: '120px' }} title={`Next: ${new Date(report.nextGeneration).toLocaleDateString()}`}>
            Next: {new Date(report.nextGeneration).toLocaleDateString()}
          </small>
        </div>
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (value, report) => {
        if (!report || !report.id) {
          console.error('Invalid report data in render function:', report);
          return <span style={{ color: 'red' }}>Error</span>;
        }
        return (
          <ActionDropdown
            report={report}
            onView={handleView}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />
        );
      }
    }
  ];

  return (
    <>
      {/* Breadcrumb - Outside page-container to align with sidebar */}
      <PageBreadcrumb />
      {/* Page Header - Outside page-container to align with sidebar */}
      <PageHeader 
        title="Reports & Analytics"
        description="Generate comprehensive reports and analytics for subcontracting operations"
        actions={headerActions}
      />
      <div className="page-container">
        {error && (
          <div className="alert alert-danger alert-dismissible fade show" role="alert">
            <strong>Error:</strong> {error}
            <button 
              type="button" 
              className="btn-close" 
              onClick={() => setError(null)} 
              aria-label="Close"
            ></button>
          </div>
        )}
      {/* Reports & Analytics Table */}
      <DataTable
        data={reportsData}
        columns={columns}
        title="Reports & Analytics"
        loading={loading}
        pagination={pagination}
        onPaginationChange={setPagination}
        showActions={false}
        emptyMessage="No reports found. Click 'Generate Report' to get started."
      />
      </div>
    </>
  );
};

export default ReportsAnalytics;
